export class CacheError extends Error {}

export class CacheKeyAssembleError extends CacheError {}

export const CACHE_KEY_SEPARATOR = ':';

export function assembleCacheKey(...parts: string[]): string {
  if (!parts.length) {
    throw new CacheKeyAssembleError('No parts to assemble');
  }

  for (const part of parts) {
    if (part.trim().length === 0) {
      throw new CacheKeyAssembleError('Unable to assemble cache key. One of the parts to assemble is empty');
    }

    if (part.includes(CACHE_KEY_SEPARATOR)) {
      throw new CacheKeyAssembleError(
        `Unable to assemble cache key. The separator "${CACHE_KEY_SEPARATOR}" has been found in a part to assemble ("${part}")`,
      );
    }
  }

  return parts.join(CACHE_KEY_SEPARATOR);
}

export default class Cache<T = unknown> {
  readonly #cache = new Map<string, T>();

  has(key: string): boolean {
    return this.#cache.has(key);
  }

  get(key: string): T | undefined {
    return this.#cache.get(key);
  }

  set(key: string, value: T): void {
    this.#cache.set(key, value);
  }

  getOrSet(key: string, cb: () => T): T {
    const actualValue = this.get(key);

    if (actualValue === undefined) {
      const newValue = cb();

      this.set(key, newValue);

      return newValue;
    }

    return actualValue;
  }

  clear(key: string): void {
    this.#cache.delete(key);
  }

  clearRegExp(keyPattern: string | RegExp): void {
    const regExp = new RegExp(keyPattern);

    for (const key of Array.from(this.#cache.keys())) {
      if (regExp.exec(key)) {
        this.#cache.delete(key);
      }
    }
  }

  clearAll(): void {
    this.#cache.clear();
  }
}
